import type { MappingData } from 'api/fetchCommon';
import type { LCalculationSummary } from 'api/submitLtool';
import { getMappingLabel } from 'utils/mappingLabel';

// L 計算サマリの表示 view model。R 側（rCalcConditionResult）と同じ 4 分類だが、
// L は複数 unit をまとめて計算するため unit 単位でグルーピングして見せる（issue #32 項 3）。
export type UnitConditionGroup = { unitLabel: string; conditions: string[] };
export type LCalcConditionResult = {
    succeeded: UnitConditionGroup[];
    missing_data: UnitConditionGroup[];
    failed: UnitConditionGroup[];
};

// condition_id は `<phenomenon_id>-<drive_mode_id>`（値域はいずれもハイフンを含まないため
// split('-') で確定）。R 側 rCalcConditionResult と同一ロジック。
const buildConditionLabel = (conditionId: string, mapping: MappingData | null): string => {
    const [phenomenonId, driveModeId] = conditionId.split('-');
    return `${getMappingLabel(mapping, 'drive_mode_id', driveModeId)} × ${getMappingLabel(mapping, 'phenomenon_id', phenomenonId)}`;
};

// 分類内の要素を unit_id で束ね、unit の出現順を保ったままラベル化する。
const groupByUnit = (
    items: { unit_id: string; condition_id: string }[],
    mapping: MappingData | null,
): UnitConditionGroup[] => {
    const byUnit = new Map<string, string[]>();
    items.forEach(({ unit_id, condition_id }) => {
        const labels = byUnit.get(unit_id) ?? [];
        labels.push(buildConditionLabel(condition_id, mapping));
        byUnit.set(unit_id, labels);
    });
    return Array.from(byUnit, ([unitId, conditions]) => ({
        unitLabel: getMappingLabel(mapping, 'unit_id', unitId),
        conditions,
    }));
};

// L 計算 API の 4 分類サマリから表示用 view model へ変換する。
// succeeded / missing_data / failed を unit 単位グルーピングで表示、skipped_no_data は非表示。
// 付随詳細（reason / missing_dataset_ids）は画面に出さず、原因調査のため開発者向けログに残す
// （ユーザー向け文言と開発者向け詳細の分離方針。R 側と同じ）。
export const buildLCalcConditionResult = (
    summary: LCalculationSummary,
    mapping: MappingData | null,
): LCalcConditionResult => {
    if (summary.failed.length > 0 || summary.missing_data.length > 0) {
        console.error('計算サマリ詳細:', {
            failed: summary.failed,
            missing_data: summary.missing_data,
        });
    }
    return {
        succeeded: groupByUnit(summary.succeeded, mapping),
        missing_data: groupByUnit(summary.missing_data, mapping),
        failed: groupByUnit(summary.failed, mapping),
    };
};
